import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import {
	createResource,
	useResource,
	type RouteResource,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';

type AssetsVerticalVerticalType = 'HAM' | (string & {});

export type VerticalInstantiationCategoryNode = {
	readonly id: string;
	readonly category: string;
	readonly status?: string | null;
	readonly spaceDetails?: {
		readonly key?: string | null;
	} | null;
} | null;

export type VerticalInstantiationNode = {
	readonly id: string;
	readonly type: AssetsVerticalVerticalType;
	readonly status?: string | null;
	readonly canRetry?: boolean | null;
	readonly categories: {
		readonly edges: ReadonlyArray<{
			readonly node: VerticalInstantiationCategoryNode;
		}>;
	} | null;
} | null;

export type VerticalInstantiationEdge = {
	readonly node: VerticalInstantiationNode;
};

type VerticalInstantiationsQueryResponse = {
	readonly assetsVertical_verticalInstantiations: {
		readonly edges: ReadonlyArray<VerticalInstantiationEdge>;
	} | null;
} | null;

export type VerticalInstantiationsResult =
	NonNullable<VerticalInstantiationsQueryResponse>['assetsVertical_verticalInstantiations'];

type VerticalInstantiationQueryOperation = {
	readonly data?: VerticalInstantiationsQueryResponse;
	readonly errors?: ReadonlyArray<{
		readonly message?: string;
	}>;
};

type VerticalInstantiationGraphQlRequest = {
	readonly query: string;
	readonly variables: {
		readonly cloudId: string;
		readonly type: AssetsVerticalVerticalType;
	};
};

export const RESOURCE_TYPE_ITAM_VERTICAL_INSTANTIATION = 'ITAM_VERTICAL_INSTANTIATION';

/** A HAM vertical instantiation category is only considered enabled once its provisioning has finished. */
export const HAM_INSTANTIATION_COMPLETED_STATUS = 'COMPLETED';

const verticalInstantiationQuery = `
	query verticalInstantiationResourceQuery($cloudId: ID!, $type: AssetsVerticalVerticalType!) {
		assetsVertical_verticalInstantiations(cloudId: $cloudId, first: 100, type: $type)
			@optIn(to: "AssetsVerticalVerticalInstantiations") {
			pageInfo {
				endCursor
			}
			edges {
				node {
					id
					type
					status
					canRetry
					categories(first: 100) {
						edges {
							node {
								id
								category
								status
								spaceDetails {
									key
								}
							}
						}
					}
				}
			}
		}
	}
`;

/**
 * Fetches the tenant's HAM vertical instantiations via GraphQL.
 * Shared by the route resource (below) and any component that needs to load
 * this data on demand (e.g. the ITAM template banner).
 */
export const fetchVerticalInstantiations = async (
	cloudId: string,
): Promise<VerticalInstantiationsResult> => {
	if (!cloudId) {
		throw new Error('Unable to fetch vertical instantiations: missing cloudId');
	}

	const requestBody: VerticalInstantiationGraphQlRequest = {
		query: verticalInstantiationQuery,
		variables: { cloudId, type: 'HAM' },
	};

	const response = await performPostRequest<VerticalInstantiationQueryOperation>(
		'/gateway/api/graphql',
		{
			body: JSON.stringify(requestBody),
			headers: {
				'content-type': 'application/json',
				'Atl-CloudId': cloudId,
			},
		},
	);

	return response.data?.assetsVertical_verticalInstantiations ?? null;
};

export const verticalInstantiationResource: RouteResource<{
	readonly edges: ReadonlyArray<VerticalInstantiationEdge>;
} | null> = createResource<VerticalInstantiationsResult>({
	type: RESOURCE_TYPE_ITAM_VERTICAL_INSTANTIATION,
	maxAge: Infinity,
	getKey: () => 'state',
	getData: async (_routerContext, { tenantContext }) => {
		const cloudId = tenantContext?.cloudId;
		if (!cloudId) {
			throw new Error('Unable to fetch vertical instantiations: missing tenantContext.cloudId');
		}

		return fetchVerticalInstantiations(cloudId);
	},
});

export function useVerticalInstantiationResource(): UseResourceHookResponse<{
	readonly edges: ReadonlyArray<VerticalInstantiationEdge>;
} | null> {
	return useResource(verticalInstantiationResource);
}
